import { getDocument } from '@atlaskit/browser-apis';
import { themeImportMap } from '@atlaskit/tokens/artifacts/theme-import-map';
import type { ThemeOverrideIds } from '@atlaskit/tokens/theme-config';

export const FINESSE_EXAMPLE_THEME_ATTRIBUTE = 'data-finesse-example-theme';

const FINESSE_EXAMPLE_THEME_STYLE_ATTRIBUTE = 'data-finesse-example-theme-style';

const FINESSE_THEME_IDS = [
	'light-finesse',
	'dark-finesse',
	'typography-finesse',
	'light-increased-contrast-finesse',
	'dark-increased-contrast-finesse',
] as const satisfies readonly ThemeOverrideIds[];

/**
 * Scopes generated theme override CSS to Finesse-enabled examples.
 *
 * Generated themes target both the page root and subtree theme providers. Examples always render
 * inside a subtree theme provider, so only those selectors are retained and extended with the
 * example attribute. This prevents one example's toggle from changing the rest of the page.
 */
const scopeFinesseThemeCss = (themeCss: string): string => {
	const declarationStart = themeCss.indexOf('{');
	if (declarationStart === -1) {
		return '';
	}

	const scopedSelectors = themeCss
		.slice(0, declarationStart)
		.split(',')
		.map((selector) => selector.trim())
		.filter((selector) => selector.startsWith('[data-subtree-theme]'))
		.map((selector) => `${selector} [${FINESSE_EXAMPLE_THEME_ATTRIBUTE}]`);

	if (scopedSelectors.length === 0) {
		return '';
	}

	return `${scopedSelectors.join(',\n')} ${themeCss.slice(declarationStart)}`;
};

/**
 * Lazily loads the Finesse override themes used by component examples.
 */
export const loadFinesseExampleThemeStyles = async (): Promise<void> => {
	const doc = getDocument();
	if (!doc) {
		return;
	}

	for (const themeId of FINESSE_THEME_IDS) {
		const selector = `style[${FINESSE_EXAMPLE_THEME_STYLE_ATTRIBUTE}="${themeId}"]`;
		if (doc.head.querySelector(selector)) {
			continue;
		}

		const { default: themeCss } = await themeImportMap[themeId]();
		const scopedThemeCss = scopeFinesseThemeCss(themeCss);
		if (!scopedThemeCss || doc.head.querySelector(selector)) {
			continue;
		}

		const style = doc.createElement('style');
		style.textContent = scopedThemeCss;
		style.setAttribute(FINESSE_EXAMPLE_THEME_STYLE_ATTRIBUTE, themeId);
		doc.head.appendChild(style);
	}
};
